import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getSmartDateDisplay } from "@/lib/dateUtils";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { useFamily } from "@/hooks/useFamily";
import { useUniversalContacts } from "@/hooks/useUniversalContacts";
import { MultipleAssigneeAvatars } from "@/components/tasks/MultipleAssigneeAvatars";
import { useNativeConfirmation } from "@/hooks/useNativeConfirmation";

import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TaskPreview {
  id: string;
  title: string;
  due_date: string | null;
  assigned_profile_ids: string[];
  assignees?: Array<{
    profile_id: string;
    avatar_url?: string;
    initials: string;
  }>;
}

interface SwipeableTaskItemProps {
  task: TaskPreview;
  onTaskClick: () => void;
  onTaskCompleted?: () => void;
  canComplete: boolean;
  completeTask: (taskId: string) => Promise<void>;
  isDraggable?: boolean;
  currentUserProfileId?: string;
  taskCreatedBy?: string;
}

export function SwipeableTaskItem({ 
  task, 
  onTaskClick, 
  onTaskCompleted, 
  canComplete, 
  completeTask,
  isDraggable = false,
  currentUserProfileId,
  taskCreatedBy
}: SwipeableTaskItemProps) {
  const { profile, family } = useFamily();
  const { contacts } = useUniversalContacts(family?.id);
  const { showConfirmation } = useNativeConfirmation();
  const smartDate = getSmartDateDisplay(task.due_date);

  // Check if confirmation is needed (user is not assigned and not creator)
  const needsConfirmation = task.assigned_profile_ids && 
    !task.assigned_profile_ids.includes(currentUserProfileId || '') && 
    taskCreatedBy !== currentUserProfileId && 
    currentUserProfileId;
  
  // Get assigned contacts using universal contacts system, excluding current user
  const assignedContacts = contacts.filter(contact => {
    const isAssigned = task.assigned_profile_ids?.includes(
      contact.source === 'family_member' 
        ? contact.profile_id || '' 
        : contact.originalData?.profile_id || contact.originalData?.id || ''
    );
    const isCurrentUser = contact.source === 'family_member' 
      ? contact.profile_id === profile?.id
      : contact.originalData?.profile_id === profile?.id;
    return isAssigned && !isCurrentUser;
  });

  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  const handleCompleteTask = async () => {
    if (needsConfirmation) {
      // Get assigned member name for confirmation
      const assignedMemberName = assignedContacts.length > 0 
        ? assignedContacts[0].displayName 
        : 'quelqu\'un d\'autre';
      
      const confirmed = await showConfirmation({
        title: "Marquer comme terminée",
        message: `Cette tâche est assignée à ${assignedMemberName}. Êtes-vous sûr de vouloir la marquer comme terminée ?`,
        okButtonTitle: "Confirmer", 
        cancelButtonTitle: "Annuler"
      });
      
      if (!confirmed) {
        return;
      }
    }
    
    try {
      await completeTask(task.id);
      onTaskCompleted?.();
    } catch (error) {
      console.error('Erreur lors de la completion de la tâche:', error);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Empêcher le clic si on a fait un swipe
    if (Math.abs(dragX) > 5) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onTaskClick();
  };

  const SWIPE_THRESHOLD = 60;
  const { handleTouchStart, handleTouchMove, handleTouchEnd, dragX, isCompleting, isCompleted, isTouching } = useSwipeGesture({
    onSwipeLeft: canComplete && !isDragging ? handleCompleteTask : undefined,
    threshold: SWIPE_THRESHOLD
  });

  const dragProgress = Math.min(Math.abs(dragX) / SWIPE_THRESHOLD, 1);
  const isDraggingLeft = dragX < -3; // More sensitive detection
  const isNearThreshold = dragProgress > 0.7; // threshold indicator
  const isOverThreshold = Math.abs(dragX) >= SWIPE_THRESHOLD;

  // Masquer la tâche si elle est terminée
  if (isCompleted) {
    return null;
  }

  return (
    <div className="relative" ref={setNodeRef} style={style}>
      {/* Progress bar indicator */}
      {isDraggingLeft && canComplete && (
        <div className="absolute top-0 left-0 h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-100 ease-out z-10"
             style={{ width: `${Math.min(dragProgress * 100, 100)}%` }} />
      )}
      
      <div 
        className={`relative w-full border border-panel rounded-lg p-3 bg-panel hover:bg-panel-hover overflow-hidden transition-all duration-200 cursor-pointer min-h-[2.5rem] ${
          isCompleting ? 'animate-slide-out-left' : ''
        } ${
          isTouching ? 'scale-[1.02] shadow-lg' : ''
        } ${
          isDraggingLeft && canComplete 
            ? isOverThreshold 
              ? 'border-green-400/60 bg-green-500/20 shadow-green-500/20 shadow-lg' 
              : isNearThreshold 
                ? 'border-green-400/50 bg-green-500/15'
                : 'border-green-400/30 bg-green-500/10'
            : 'border-border hover:bg-slate-100 hover:border-border/80'
        } ${
          isDragging ? 'opacity-50 scale-105 rotate-1 shadow-lg z-50' : ''
        } ${
          isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        style={{
          transform: isDraggable 
            ? CSS.Transform.toString(transform)
            : `translateX(${dragX}px) ${isTouching && !isDraggingLeft ? 'scale(1.01)' : ''}`,
          transition: isDraggable 
            ? transition
            : dragX !== 0 ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          filter: isDraggingLeft && canComplete ? `brightness(${1 + dragProgress * 0.2})` : ''
        }}
        onClick={!isDragging ? handleClick : undefined}
        {...(!isDraggable ? { onTouchStart: (e: React.TouchEvent) => { e.stopPropagation(); handleTouchStart(e); } } : {})}
        {...(!isDraggable ? { onTouchMove: (e: React.TouchEvent) => { e.stopPropagation(); handleTouchMove(e); } } : {})}
        {...(!isDraggable ? { onTouchEnd: (e: React.TouchEvent) => { e.stopPropagation(); handleTouchEnd(e); } } : {})}
        {...(isDraggable ? { ...attributes, ...listeners } : {})}
      >
        {/* Enhanced check icon with scaling and color transition */}
        {isDraggingLeft && canComplete && (
          <div 
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-all duration-100 ease-out ${
              isOverThreshold ? 'text-green-500 animate-pulse' : 'text-green-600'
            }`}
            style={{ 
              opacity: Math.min(dragProgress * 1.2, 1),
              transform: `translateY(-50%) scale(${0.7 + dragProgress * 0.5}) ${isOverThreshold ? 'rotate(5deg)' : ''}`
            }}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            
            {/* Threshold reached indicator */}
            {isNearThreshold && (
              <div className="absolute -inset-1 bg-green-400/30 rounded-full animate-ping" />
            )}
          </div>
        )}

        {/* Completion success animation */}
        {isCompleting && (
          <div className="absolute inset-0 flex items-center justify-center bg-green-500/90 rounded-lg">
            <svg className="w-8 h-8 text-white animate-bounce" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 space-y-1 pr-3">
            <h4 className="font-medium text-sm leading-tight text-foreground break-words line-clamp-2">
              {task.title}
            </h4>
            
            {/* Date below title with new colors for urgency */}
            {smartDate && (
              <div className="text-xs">
                <span className={`${
                  smartDate === "En retard" || smartDate.startsWith("Il y a") ? 'text-destructive font-medium' :
                  smartDate === "Aujourd'hui" || smartDate === "Hier" ? 'text-orange-600 font-medium' :
                  smartDate === "Demain" ? 'text-primary' :
                  'text-muted-foreground'
                }`}>
                  {smartDate}
                </span>
              </div>
            )}
          </div>

          {/* Right: Assignee avatars */}
          <div className="flex-shrink-0">
            <MultipleAssigneeAvatars 
              assignedContacts={assignedContacts}
              size="sm"
              maxVisible={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
}